// Forked from https://github.com/promplate/partial-json-parser-js/blob/main/src/index.ts
// Cc changes:
// 1. Configure allowed completions based on key name instead of value type.
// 2. Option to mark allowed completions with isDone.
//
// In the future, we could modify this to parse incrementally instead of re-parsing
// the whole input on each delta.

class PartialJSON extends Error {}

class MalformedJSON extends Error {}

/**
 * Parse incomplete JSON
 * @param {string} jsonString Partial JSON to be parsed
 * @param allowedPartialKeys Function that inputs a key name and outputs whether its value
 * is allowed to be partial:
 * - false: No, the key will be omitted.
 * - true: Yes, the key will be mapped to its partial value.
 * - "mark": Yes, the key will be mapped to { isDone, value: (partial value) }.
 * **Currently only implemented for string values.**
 * @returns The parsed JSON
 * @throws {PartialJSON} If the JSON is incomplete (related to the `allow` parameter)
 * @throws {MalformedJSON} If the JSON is malformed
 */
export function parsePartialJSON(
  jsonString: string,
  allowedPartialKeys: (keyName: string) => boolean | "mark"
): any {
  if (typeof jsonString !== "string") {
    throw new TypeError(`expecting str, got ${typeof jsonString}`)
  }
  if (!jsonString.trim()) {
    throw new Error(`${jsonString} is empty`)
  }
  return _parseJSON(jsonString.trim(), allowedPartialKeys)
}

const _parseJSON = (
  jsonString: string,
  allowedPartialKeys: (keyName: string) => boolean | "mark"
) => {
  const length = jsonString.length
  let index = 0

  const markPartialJSON = (msg: string) => {
    throw new PartialJSON(`${msg} at position ${index}`)
  }

  const throwMalformedError = (msg: string) => {
    throw new MalformedJSON(`${msg} at position ${index}`)
  }

  const parseAny = (allowPartialHere: boolean | "mark"): any => {
    skipBlank()
    if (index >= length) markPartialJSON("Unexpected end of input")
    if (jsonString[index] === '"') return parseStr(allowPartialHere)
    if (jsonString[index] === "{") return parseObj(!!allowPartialHere)
    if (jsonString[index] === "[") return parseArr(!!allowPartialHere)
    if (
      jsonString.substring(index, index + 4) === "null" ||
      (allowPartialHere && length - index < 4 && "null".startsWith(jsonString.substring(index)))
    ) {
      index += 4
      return null
    }
    if (
      jsonString.substring(index, index + 4) === "true" ||
      (allowPartialHere && length - index < 4 && "true".startsWith(jsonString.substring(index)))
    ) {
      index += 4
      return true
    }
    if (
      jsonString.substring(index, index + 5) === "false" ||
      (allowPartialHere && length - index < 5 && "false".startsWith(jsonString.substring(index)))
    ) {
      index += 5
      return false
    }
    if (
      jsonString.substring(index, index + 8) === "Infinity" ||
      (allowPartialHere && length - index < 8 && "Infinity".startsWith(jsonString.substring(index)))
    ) {
      index += 8
      return Infinity
    }
    if (
      jsonString.substring(index, index + 9) === "-Infinity" ||
      (allowPartialHere &&
        1 < length - index &&
        length - index < 9 &&
        "-Infinity".startsWith(jsonString.substring(index)))
    ) {
      index += 9
      return -Infinity
    }
    if (
      jsonString.substring(index, index + 3) === "NaN" ||
      (allowPartialHere && length - index < 3 && "NaN".startsWith(jsonString.substring(index)))
    ) {
      index += 3
      return NaN
    }
    return parseNum(!!allowPartialHere)
  }

  const parseStr = (allowPartialHere: boolean | "mark"): any => {
    const start = index
    let escape = false
    index++ // skip initial quote
    while (
      index < length &&
      (jsonString[index] !== '"' || (escape && jsonString[index - 1] === "\\"))
    ) {
      escape = jsonString[index] === "\\" ? !escape : false
      index++
    }
    if (jsonString.charAt(index) == '"') {
      try {
        const value = JSON.parse(jsonString.substring(start, ++index - Number(escape)))
        return allowPartialHere === "mark" ? { isDone: true, value } : value
      } catch (e) {
        throwMalformedError(String(e))
      }
    } else if (allowPartialHere) {
      let value: string
      try {
        value = JSON.parse(jsonString.substring(start, index - Number(escape)) + '"')
      } catch (e) {
        // SyntaxError: Invalid escape sequence
        value = JSON.parse(jsonString.substring(start, jsonString.lastIndexOf("\\")) + '"')
      }
      return allowPartialHere === "mark" ? { isDone: false, value } : value
    }
    markPartialJSON("Unterminated string literal")
  }

  const parseObj = (allowPartialHere: boolean): any => {
    index++ // skip initial brace
    skipBlank()
    const obj: Record<string, any> = {}
    try {
      while (jsonString[index] !== "}") {
        skipBlank()
        if (index >= length && allowPartialHere) return obj
        const key = parseStr(false)
        skipBlank()
        index++ // skip colon
        try {
          const value = parseAny(allowedPartialKeys(key))
          obj[key] = value
        } catch (e) {
          if (allowPartialHere) return obj
          else throw e
        }
        skipBlank()
        if (jsonString[index] === ",") index++ // skip comma
      }
    } catch (e) {
      if (allowPartialHere) return obj
      else markPartialJSON("Expected '}' at end of object")
    }
    index++ // skip final brace
    return obj
  }

  const parseArr = (allowPartialHere: boolean): any => {
    index++ // skip initial bracket
    const arr: any[] = []
    try {
      while (jsonString[index] !== "]") {
        arr.push(parseAny(allowPartialHere))
        skipBlank()
        if (jsonString[index] === ",") {
          index++ // skip comma
        }
      }
    } catch (e) {
      if (allowPartialHere) {
        return arr
      }
      markPartialJSON("Expected ']' at end of array")
    }
    index++ // skip final bracket
    return arr
  }

  const parseNum = (allowPartialHere: boolean): any => {
    if (index === 0) {
      if (jsonString === "-") throwMalformedError("Not sure what '-' is")
      try {
        return JSON.parse(jsonString)
      } catch (e) {
        if (allowPartialHere)
          try {
            return JSON.parse(jsonString.substring(0, jsonString.lastIndexOf("e")))
          } catch (e) {
            // Fall through
          }
        throwMalformedError(String(e))
      }
    }

    const start = index

    if (jsonString[index] === "-") index++
    while (jsonString[index] && ",]}".indexOf(jsonString[index]) === -1) index++

    if (index == length && !allowPartialHere) markPartialJSON("Unterminated number literal")

    try {
      return JSON.parse(jsonString.substring(start, index))
    } catch (e) {
      if (jsonString.substring(start, index) === "-") markPartialJSON("Not sure what '-' is")
      try {
        return JSON.parse(jsonString.substring(start, jsonString.lastIndexOf("e")))
      } catch (e) {
        throwMalformedError(String(e))
      }
    }
  }

  const skipBlank = () => {
    while (index < length && " \n\r\t".includes(jsonString[index])) {
      index++
    }
  }
  return parseAny(true)
}
